import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { WindowRef } from '@progress/kendo-angular-dialog';
import { Connection } from '../obj/Connection';
import { FlowManagementService } from '../services/flow-management.service';
import { mapWithConcurrency } from '../services/concurrency.util';
import { ChildFlowRow } from './flows.child-flows.component';

/**
 * One row in the popup — represents a connector (e.g. `shared_sharepointonline`)
 * whose Invoker connection references appear in at least one of the selected
 * child flows. The user picks ONE replacement connection per connector; all
 * Invoker refs that point at that connector across all selected flows get
 * rewritten to use the picked connection.
 */
interface ConnectorReplacement {
  api: string;
  /** Distinct ref keys across selected flows that bind to this connector with source=Invoker. */
  refKeysAcrossFlows: Set<string>;
  /** Number of selected flows that contain at least one Invoker ref for this connector. */
  flowCount: number;
  available: Connection[];
  selected: Connection | null;
}

interface PerFlowResult {
  flowName: string;
  displayName: string;
  status: 'pending' | 'saving' | 'ok' | 'skipped' | 'error';
  message?: string;
}

@Component({
  selector: 'app-flows-child-flows-replace-connection',
  templateUrl: './flows.child-flows-replace-connection.component.html',
  styleUrl: './flows.child-flows-replace-connection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChildFlowsReplaceConnectionComponent implements OnInit {

  constructor(
    private flowManagementService: FlowManagementService,
    public windowRef: WindowRef,
    private cdr: ChangeDetectorRef,
  ) {}

  @Input() rows: ChildFlowRow[] = [];
  @Input() environment: string = '';
  @Output() applied = new EventEmitter<string[]>();

  loading = true;
  applying = false;
  error: string | null = null;
  connectors: ConnectorReplacement[] = [];
  perFlow: PerFlowResult[] = [];

  async ngOnInit() {
    try {
      this.perFlow = (this.rows || []).map(r => ({
        flowName: r.flowName,
        displayName: r.displayName,
        status: 'pending',
      }));

      // Build the distinct-connector map by walking each selected row's
      // connectionRefs and collecting only the Invoker ones. We track:
      //   - which ref-keys (per-flow) hit each connector (informational)
      //   - how many flows touch the connector (so the user can size the impact)
      const byApi = new Map<string, ConnectorReplacement>();
      for (const row of this.rows) {
        const invokerForRow = row.connectionRefs.filter(c => c.mode === 'Invoker' && c.api);
        const seenInRow = new Set<string>();
        for (const ref of invokerForRow) {
          let entry = byApi.get(ref.api);
          if (!entry) {
            entry = {
              api: ref.api,
              refKeysAcrossFlows: new Set<string>(),
              flowCount: 0,
              available: [],
              selected: null,
            };
            byApi.set(ref.api, entry);
          }
          entry.refKeysAcrossFlows.add(ref.name);
          seenInRow.add(ref.api);
        }
        for (const api of seenInRow) byApi.get(api)!.flowCount++;
      }

      // Fetch user connections for the environment once, then bucket by
      // connector. Connection.connector strips the `/apis/` prefix so it
      // matches what we collected from the flow connectionRefs above.
      let userConnections: Connection[] = [];
      try {
        userConnections = await this.flowManagementService.getConnectionsUser(this.environment);
      } catch (ex) {
        console.error('[ReplaceConnection] getConnectionsUser failed', ex);
        this.error = `Failed to load user connections: ${(ex as any)?.message || ex}`;
      }

      for (const entry of byApi.values()) {
        entry.available = userConnections
          .filter(c => c.connector === entry.api)
          .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
      }

      this.connectors = Array.from(byApi.values()).sort((a, b) => a.api.localeCompare(b.api));
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  /** Disable Apply until the user has picked a replacement for at least one connector. */
  canApply(): boolean {
    return !this.applying && !this.loading && this.connectors.some(c => this.isRealPick(c.selected));
  }

  /** True after the user has picked an actual user connection (not the "leave unchanged" sentinel). */
  hasResults(): boolean {
    return this.perFlow.some(p => p.status !== 'pending');
  }

  private isRealPick(c: Connection | any): boolean {
    return !!(c && c.name);
  }

  cancel() {
    this.windowRef.close();
  }

  /**
   * Walk every selected row, fetch its full definition + connectionReferences,
   * rewrite each Invoker ref whose connector has a user-chosen replacement,
   * then PATCH the flow. We fan out at concurrency 4 to be polite to the PA API.
   */
  async apply() {
    const replacementsByApi = new Map<string, Connection>();
    for (const c of this.connectors) {
      if (this.isRealPick(c.selected)) replacementsByApi.set(c.api, c.selected!);
    }
    if (replacementsByApi.size === 0) return;

    this.applying = true;
    this.error = null;
    this.cdr.markForCheck();

    const updated: string[] = [];

    await mapWithConcurrency(this.rows, async (row) => {
      const status = this.perFlow.find(p => p.flowName === row.flowName);
      if (status) { status.status = 'saving'; this.cdr.markForCheck(); }
      try {
        const full: any = await this.flowManagementService.getFlow(row.environment, row.flowName).toPromise();
        const props = full?.properties;
        const connRefs = props?.connectionReferences;
        if (!props || !connRefs) {
          if (status) { status.status = 'skipped'; status.message = 'no connectionReferences on flow'; }
          return;
        }

        // Rewrite every Invoker ref whose connector we have a target for.
        // We deliberately leave non-Invoker refs alone and we preserve any
        // existing fields on the ref (api/id/tier) so we don't strip metadata
        // the PA backend may want round-tripped.
        let changed = 0;
        for (const [key, refRaw] of Object.entries<any>(connRefs)) {
          if (!refRaw || refRaw.source !== 'Invoker') continue;
          const apiName: string = refRaw?.api?.name || refRaw?.id?.split('/').pop() || '';
          const target = replacementsByApi.get(apiName);
          if (!target) continue;
          connRefs[key] = {
            ...refRaw,
            connectionName: target.name,
            connection: { name: target.name, id: target.id },
            source: 'Embedded',
          };
          changed++;
        }

        if (changed === 0) {
          if (status) { status.status = 'skipped'; status.message = 'no matching Invoker refs'; }
          return;
        }

        // PATCH minimal body — just connectionReferences. The PA backend
        // accepts a partial properties patch; keeping the body small avoids
        // accidentally clobbering other properties the user has set.
        const body = { properties: { connectionReferences: connRefs } };
        await this.flowManagementService.updateFlow(row.environment, row.flowName, body).toPromise();

        updated.push(row.flowName);
        if (status) { status.status = 'ok'; status.message = `${changed} ref(s) updated`; }
      } catch (ex: any) {
        console.error('[ReplaceConnection] update failed for', row.flowName, ex);
        if (status) { status.status = 'error'; status.message = ex?.message || String(ex); }
      } finally {
        this.cdr.markForCheck();
      }
    }, { concurrency: 4 });

    this.applying = false;
    this.cdr.markForCheck();
    this.applied.emit(updated);

    // Auto-close only if everything succeeded; otherwise leave the dialog
    // open so the user can read the per-flow error messages.
    if (this.perFlow.every(p => p.status === 'ok' || p.status === 'skipped')) {
      this.windowRef.close();
    }
  }

  // template helpers
  trackByApi = (_: number, c: ConnectorReplacement) => c.api;
  trackByFlow = (_: number, r: PerFlowResult) => r.flowName;
  connectionLabel = (c: Connection): string => c?.displayName || c?.name || '';
}
